import cx from 'classnames'
import { useEffect, useRef } from 'react'
import styles from './DropdownMenu.module.scss'

/**
 * @typedef {Object} DropdownOption
 * @property {string|number} key - Unique id passed back to onSelect.
 * @property {import('react').ReactNode} label - Text/content shown in the row.
 * @property {string} [icon] - Optional icon path rendered before the label.
 */

/**
 * Reusable, MUI-free dropdown menu. Controlled by the parent (isOpen/onClose).
 * Renders nothing when closed. Closes on outside click and after a selection.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the menu is visible.
 * @param {DropdownOption[]} props.options - Items to render.
 * @param {(key: string|number) => void} props.onSelect - Called with the chosen option's key.
 * @param {() => void} props.onClose - Called to close the menu (outside click / after select).
 * @param {string} [props.customClass] - Consumer-specific root class; defaults to drop-below, right-aligned.
 * @param {string} [props.listClassName] - Extra class for the menu list (<ul>), e.g. to change its border.
 * @param {import('react').RefObject} [props.triggerRef] - Ref of the trigger; clicks inside it won't close the menu (so a toggle trigger works).
 */
export const DropdownMenu = ({
  isOpen,
  options,
  onSelect,
  onClose,
  customClass,
  listClassName,
  triggerRef
}) => {
  const dropdownRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = event => {
      const insideMenu = dropdownRef.current && dropdownRef.current.contains(event.target)
      const insideTrigger = triggerRef?.current && triggerRef.current.contains(event.target)
      if (!insideMenu && !insideTrigger) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleSelect = option => {
    onSelect(option.key)
    onClose()
  }

  return (
    <div
      ref={dropdownRef}
      className={cx(styles.dropdownMenuContainer, customClass || styles.defaultPosition)}
    >
      <ul className={cx(styles.dropdownMenu, listClassName)}>
        {options.map(option => (
          <li
            key={option.key}
            onClick={() => handleSelect(option)}
          >
            <div className={styles.iconContainer}>
              <img
                src={option.icon}
                className={styles.icon}
              />
            </div>
            <span className={styles.label}>{option.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
